import axios from "axios";
import * as authService from "../services/auth";
import * as auth from "../util/auth";
import { decryptData, encryptData } from "./encryptData";
import { getCookie, setCookie } from "../util/session-cookie.util";
import { toast } from "react-toastify";

// Flag
let refreshing = false;

let middlewares = {
  request: [],
  response: [],
};
const urlsToExcludeForbiddenStatus = [
  "api/authenticate/tokenValidate",
  "/api/v2/iv/realtime",
];
const responseErrorCodes = [400, 401, 403, 404, 406, 500];

export const addMiddleware = (type, fn) => {
  middlewares[type].push(fn);
};

export const removeMiddleware = (type, fn) => {
  middlewares[type] = middlewares[type].filter((x) => x !== fn);
};

export const Api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  header: {
    "Content-Type": "application/json",
  },
});

Api.defaults.headers.put["Content-Type"] = "application/json";
Api.defaults.headers.post["Content-Type"] = "application/json";

// Handle Request
Api.interceptors.request.use(async (cfg) => {
  const config = middlewares["request"].reduce((a, el) => {
    return el(a);
  }, cfg);

  config.headers["X-Session-Id"] = getCookie("sessionId");

  _injectBearerToken(config);

  if (process.env.REACT_APP_LOG_REQUEST)
    console.log(config.url, config.data || config.params || "-");

  // Decorate the request
  if ((config.data && config.data instanceof FormData) === false) {
    if (!config._retry) return await _encryptRequest(config);
  }

  return config;
});

// Handle Responses
Api.interceptors.response.use(
  async (response) => {
    _logoutIfBlockedOrInvalidToken(response, response.config.url.split("?")[0]);
    let originalRequest = response.config;

    if (isBinaryResponse(response)) {
      let json = null;

      try {
        const text = await getBinaryResponseText(response.data);
        json = JSON.parse(text);
      } catch (err) {
        console.log(err);
        // If it is a real file/blob/arraybuffer, return the original response.
        return response;
      }

      const blobHasErrorCode = responseErrorCodes.includes(json?.code);

      // If blob is actually a JSON error payload
      if (json?.success === false || blobHasErrorCode) {
        const errorMessage =
          json?.err || json?.message || "Dear user, please try again";
        const shouldRefreshToken =
          json.code === 401 && auth.getToken() && !originalRequest._retry;

        if (shouldRefreshToken) {
          originalRequest._retry = true;

          return _refreshToken().then((r) => {
            if (!r) throw new Error(errorMessage);
            return Api.request(originalRequest);
          });
        } else if (blobHasErrorCode) {
          toast.error(errorMessage);
          throw new Error(errorMessage);
        }

        throw new Error(errorMessage);
      }

      return response;
    }

    const errorMessage =
      response?.data?.err ||
      response?.data?.message ||
      "Dear user, please try again";

    const willRefreshToken =
      response.data.code === 401 && auth.getToken() && !originalRequest._retry;

    if (willRefreshToken) {
      originalRequest._retry = true;
      if (willRefreshToken) {
        originalRequest._retry = true;
        return _refreshToken().then((r) => {
          if (!r) throw new Error(errorMessage);
          return Api.request(originalRequest);
        });
      }
    } else if (responseErrorCodes.includes(response.data?.code)) {
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } else if (
      response.data.code === 404 &&
      urlsToExcludeForbiddenStatus.includes(response.config.url)
    )
      throw new Error(response?.data?.err || "Dear user, please try again");
    else if (
      response.data.code === 403 &&
      urlsToExcludeForbiddenStatus.includes(response.config.url)
    )
      throw new Error(response?.data?.err || "Dear user, please try again");
    else if ("value" in response.data) {
      const rawValue = response.data?.value;

      if (typeof rawValue !== "string" || !rawValue.includes("ciphertext")) {
        return response;
      }

      const value = JSON.parse(rawValue);

      const valueDecrypted = await decryptData(value);

      if ([500, 400, 401, 406, 403, 404].includes(valueDecrypted?.code)) {
        toast.error(errorMessage);
        throw new Error(valueDecrypted?.err || "Dear user, please try again");
      }

      return response;
    }
    return response;
  },
  (error) => {
    console.log("", error);
    return Promise.reject(error);
  },
);

const _refreshToken = async () => {
  if (!refreshing)
    refreshing = new Promise((resolve, reject) => {
      return authService.refreshToken().then(([status, resp]) => {
        if (!status || resp.data.success === false) {
          auth.logout();
          window.location.reload();
          return resp;
        }
        setCookie("sessionId", resp.data.sessionId);
        auth.updateToken(resp.data.token);
        resolve(true);
        refreshing = false;
      });
    }).catch((r) => {
      auth.logout();
      window.location.reload();
      return r;
    });

  return refreshing;
};

export const decryptApiResponse = (r) => {
  if (r && r.data && r.data.value) {
    return decryptData(JSON.parse(r.data.value));
  }

  return Promise.reject("");
};

export const getFile = (url, params = {}) =>
  new Promise((resolve, reject) => {
    Api.defaults.headers.get["Content-Type"] = "application/json";
    try {
      Api.get(url, {
        responseType: "blob",
        params,
      })
        .then((resp) => {
          resolve(resp);
        })
        .catch((err) => {
          reject(err);
        });
    } catch (e) {
      reject(e);
    }
  });

export const fakeResponse = async (
  returnedValue,
  asError = false,
  timer = 300,
) => {
  return await new Promise((resolve, reject) => {
    setTimeout(
      () =>
        asError === false
          ? resolve({
              data: {
                success: true,
                value: returnedValue,
              },
            })
          : reject("Error"),
      timer,
    );
  });
};

export const throwOnAxiosError = (req, message) => {
  if (typeof req === "object" && req.isAxiosError) {
    throw new Error(message);
  }
  return req;
};

async function _encryptRequest(config) {
  config.data = await encryptData(config.data);

  return config;
}

function _logoutIfBlockedOrInvalidToken(response, currentUrl) {
  if (urlsToExcludeForbiddenStatus.includes(currentUrl)) {
    return;
  }
  const userIsBlocked =
    response.data && response.data.code && response.data.code === 403;

  // Logout if a 403 is recieved response.data.code === 500
  if (userIsBlocked && response.config.url !== authService.AUTHENTICATE_URL) {
    auth.logout();
    window.location.reload();
  }

  if (response.data.code === 401 && response.data.err === "Invalid token") {
    auth.logout();
    window.location.reload();
  }
}

function _injectBearerToken(config) {
  const token = auth.getToken();

  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
}

function isBinaryResponse(response) {
  return (
    response.request?.responseType === "blob" ||
    response.request?.responseType === "arraybuffer" ||
    response.data instanceof Blob ||
    response.data instanceof ArrayBuffer
  );
}

async function getBinaryResponseText(data) {
  if (data instanceof Blob) {
    if (typeof data.text === "function") {
      return data.text();
    }

    return readBlobAsText(data);
  }

  if (data instanceof ArrayBuffer) {
    return new TextDecoder("utf-8").decode(new Uint8Array(data));
  }

  return Promise.reject(new Error("Unsupported binary response"));
}

function readBlobAsText(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}
